/**
 * ExpiringQuotesStrip ✦ iter227
 *
 * Shared chase strip — mounts on /admin/bookings AND /admin/quotes
 * top. Shows sent/draft quotes whose valid_until_date is within
 * the next 7 days. Colour bands: red ≤2d, amber 3-7d. Quotes
 * older than the window (≤-2d) also surface so Craig can chase
 * just-expired ones with one click.
 *
 * Self-contained: fetches its own data, never blocks the parent
 * page render. Quietly hides itself when no quotes match.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const BAND_STYLE = {
  green:  { dot: '#7DB58C', text: '#CFE6CC' },
  amber:  { dot: '#E2C77B', text: '#F4E2A8' },
  red:    { dot: '#E39A4F', text: '#F6CC92' },
};

const labelFor = (days) => {
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${days} days`;
};

const ExpiringQuotesStrip = ({ onView }) => {
  const [quotes, setQuotes] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/quote-pipeline/expiring`, { params: { days: 7 } })
      .then((r) => setQuotes(r.data?.quotes || []))
      .catch(() => setQuotes([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || quotes.length === 0) return null;

  return (
    <section data-testid="expiring-quotes-strip" style={{
      marginBottom: 22, padding: 'clamp(18px, 2.5vw, 24px)',
      border: `1px dashed ${GOLD_FAINT}`, borderRadius: 12,
      background: 'rgba(212,170,82,0.04)',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 12, flexWrap: 'wrap', gap: 10,
      }}>
        <div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD, marginBottom: 4 }}>
            ✦ Quotes Due Within 7 Days
          </div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, color: IVORY_DIM, lineHeight: 1.45 }}>
            Chase these today — a nudge here is usually the difference between accepted and expired.
          </div>
        </div>
        <span style={{
          padding: '4px 14px', borderRadius: 999,
          background: 'rgba(212,170,82,0.18)',
          color: GOLD, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, letterSpacing: '0.04em',
        }}>{quotes.length} to chase</span>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {quotes.map((q) => {
          const b = BAND_STYLE[q.band] || BAND_STYLE.amber;
          return (
            <div key={q.quote_id}
              data-testid={`expiring-quote-${q.quote_id}`}
              data-band={q.band}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(110px,140px) minmax(180px,1.6fr) minmax(140px,1fr) auto',
                alignItems: 'center', gap: 12,
                padding: '12px 14px', borderRadius: 10,
                background: 'rgba(0,0,0,0.18)',
                border: `1px solid ${b.dot}33`,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span aria-hidden style={{
                  width: 8, height: 8, borderRadius: 999,
                  background: b.dot,
                  boxShadow: `0 0 10px ${b.dot}88`,
                }} />
                <span style={{ fontFamily: SERIF, fontSize: 13.5, color: IVORY, fontWeight: 500 }}>
                  {q.quote_number}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: SERIF, fontSize: 14, color: IVORY, fontWeight: 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {q.customer_name || '(unnamed)'}
                </div>
                <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_DIM,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {q.customer_email}{q.venue ? ` · ${q.venue}` : ''}
                </div>
              </div>
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 13,
                color: b.text, letterSpacing: '0.02em',
              }}>
                {labelFor(q.days_remaining)}
              </div>
              <button type="button"
                onClick={() => onView?.(q.quote_id)}
                data-testid={`expiring-quote-view-${q.quote_id}`}
                style={{
                  padding: '6px 14px', borderRadius: 999,
                  border: `1px solid ${GOLD}`,
                  background: 'linear-gradient(135deg, rgba(212,170,82,0.20), rgba(184,151,98,0.10))',
                  color: IVORY, fontFamily: SERIF, fontSize: 12.5,
                  letterSpacing: '0.02em', cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}>View Quote ✦</button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ExpiringQuotesStrip;
